import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Badge,
} from "react-bootstrap";
import './commonstyle.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import { listVehiclesByCorporate, deactivateVehicle } from '../../services/VehicleService';
import '../tasks/tasklist.css';

const VehicleList = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [currentPage, setCurrentPage] = useState(1);

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000, closeOnClick: true });

  useEffect(() => {
    fetchVehicles();
  }, [currentPage, search, statusFilter]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      const corporateId = user?.corporateId || user?.id;
      
      const params = {
        page: currentPage,
        limit: 10,
      };
      
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;

      const res = await listVehiclesByCorporate(corporateId, params);
      
      if (res.data.success) {
        setVehicles(res.data.data || []);
        setPagination(res.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        });
      } else {
        setVehicles([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        });
      }
    } catch (error) {
      console.error('Vehicle list fetch error:', error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || 'Failed to fetch vehicles');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = async () => {
    if (!selectedVehicle) return;
    const userData = JSON.parse(localStorage.getItem('user'));
    setDeleteLoading(true);
    try {
      await deactivateVehicle(selectedVehicle.id, userData.id);
      notifySuccess('Vehicle deactivated successfully');
      setShowDeleteModal(false);
      setSelectedVehicle(null);
      fetchVehicles();
    } catch (error) {
      console.error('Delete vehicle error:', error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || 'Failed to deactivate vehicle');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => handlePageChange(i)}
          disabled={loading}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {(currentPage - 1) * pagination.itemsPerPage + 1} to {Math.min(currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} entries
        </div>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            Previous
          </button>
          {pages}
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.totalPages || loading}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Define columns for CommonTable
  const columns = [
    {
      key: 'registration_number',
      header: 'Registration Number',
      accessor: (row) => row.registration_number || 'N/A',
    },
    {
      key: 'vehicle_type',
      header: 'Vehicle Type',
      accessor: (row) => row.vehicle_type || 'N/A',
    },
    {
      key: 'maker',
      header: 'Maker',
      accessor: (row) => row.maker || 'N/A',
    },
    {
      key: 'model',
      header: 'Model',
      accessor: (row) => row.model || 'N/A',
    },
    {
      key: 'staff',
      header: 'Assigned Staff',
      accessor: (row) => {
        if (row.staff) {
          const staffName = row.staff.name || `${row.staff.first_name || ''} ${row.staff.last_name || ''}`.trim();
          return staffName || row.staff.employee_code || 'N/A';
        }
        return 'N/A';
      },
    },
    {
      key: 'ownership_type',
      header: 'Ownership',
      accessor: (row) => row.ownership_type || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => {
        const status = row.status || 'Requested';
        const variant = status === 'Active' ? 'success' : status === 'Expired' ? 'danger' : status === 'Deactivated' ? 'secondary' : 'warning';
        return <Badge bg={variant}>{status}</Badge>;
      },
    },
    {
      key: 'branch',
      header: 'Branch',
      accessor: (row) => row.branch_name || row.Branch?.name || 'N/A',
    },
  ];

  // Define actions for CommonTable
  const getActions = (row, rowIndex) => {
    return [
      {
        label: 'View',
        icon: 'fas fa-eye',
        onClick: () => navigate(`/vehicle/vehicleDetails/${row.id}`),
      },
      {
        label: 'Edit',
        icon: 'fas fa-edit',
        onClick: () => navigate(`/vehicle/editVehicle/${row.id}`),
      },
      {
        label: 'Deactivate',
        icon: 'fas fa-ban',
        className: 'text-warning',
        onClick: () => {
          setSelectedVehicle(row);
          setShowDeleteModal(true);
        },
      },
    ];
  };

  return (
    <Row style={{marginBottom: "0px !important"}}>
      <ToastContainer />
      <Col lg={12}>
        <Card className="user-list-card">
          <Card.Header style={{ display: 'block' }}>
            <div className="d-flex flex-row justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="search-input-group" style={{ flex: '1', minWidth: '150px' }}>
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by registration number, maker, model..."
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
                <Link to='/vehicle/addVehicle' className="new-user-btn" style={{ whiteSpace: 'nowrap' }}>
                  <i className="fas fa-plus me-2"></i>
                  Add Vehicle
                </Link>
              </div>
            </div>
          </Card.Header>
          <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1rem' }} className="p-md-3">
            {loading && vehicles.length === 0 ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <>
                <div className="row">
                  <CommonTable
                    columns={columns}
                    data={vehicles}
                    actions={getActions}
                    emptyMessage={vehicles.length === 0 ? 'No vehicles found' : 'No vehicles match your search criteria'}
                    emptyStateIcon="fas fa-car"
                    getRowId={(row, index) => row.id || index}
                    maxHeight="70vh"
                    minHeight="400px"
                    onRowClick={(row, rowIndex, e) => {
                      // Don't navigate if clicking on action button or action dropdown
                      if (e.target.closest('.table-actions') || e.target.closest('.dropdown-menu')) {
                        return;
                      }
                      navigate(`/vehicle/vehicleDetails/${row.id}`);
                    }}
                  />
                </div>
                {renderPagination()}
              </>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deactivate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate vehicle "{selectedVehicle?.registration_number || 'this vehicle'}"? This action will mark it as deactivated.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deactivating...
              </>
            ) : (
              'Deactivate'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <BackToTop />
      <InternalFooter />
    </Row>
  );
};

export default VehicleList;
